import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";
import type {
  MarketplaceAccountSettingsInput,
  MarketplaceAccountView,
  MarketplaceProviderId,
  PriceListView,
  WarehouseView,
} from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { marketplaceIn, shopeeAuthExpiringSoon } from "@cx/domain";
import { ConfirmDialog, ContentHeader, Field, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import { ACCOUNT_STATUS, PROVIDER_UI } from "./labels";
import { useMarketplaceAccounts } from "./useAccounts";

const settingsOf = (a: MarketplaceAccountView): MarketplaceAccountSettingsInput => ({
  storeName: a.storeName,
  importOrders: a.importOrders,
  reserveOnImport: a.reserveOnImport,
  syncStock: a.syncStock,
  stockWarehouseIds: a.stockWarehouseIds,
  orderWarehouseId: a.orderWarehouseId,
  syncPrice: a.syncPrice,
  priceListId: a.priceListId,
});

/** Preferências › Integrações › Mercado Livre. */
export function MercadoLivreSettingsPage(): ReactElement {
  return <MarketplaceSettingsPage provider="mercadolivre" />;
}

/** Preferências › Integrações › Shopee. */
export function ShopeeSettingsPage(): ReactElement {
  return <MarketplaceSettingsPage provider="shopee" />;
}

/** Contas conectadas de um marketplace (autorização), loja, importação e sincronização. */
function MarketplaceSettingsPage({ provider }: { provider: MarketplaceProviderId }): ReactElement {
  const { me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canCreate = can(perms, "marketplaces", "create");
  const toast = useToast();
  const ui = PROVIDER_UI[provider];
  const list = useMarketplaceAccounts();
  const connect = useMutation({
    mutationFn: () => api<{ url: string }>(`/marketplaces/${provider}/authorize`, { method: "POST" }),
    onSuccess: (r) => {
      window.location.assign(r.url);
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const mode = list.data?.modes?.[provider];
  const accounts = (list.data?.accounts ?? []).filter((a) => a.provider === provider);
  return (
    <>
      <ContentHeader
        title={`Integrações · ${ui.label}`}
        breadcrumb={["Preferências", "Integrações", ui.label]}
        actions={
          canCreate && mode && mode !== "disabled" ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={connect.isPending}
              onClick={() => connect.mutate()}
            >
              {provider === "shopee" ? "Conectar loja" : "Conectar conta"}
            </button>
          ) : undefined
        }
      />
      <QueryStatus loading={list.isLoading} error={list.error} />
      {mode === "sandbox" && (
        <div className="alert alert-info py-2">
          {provider === "shopee"
            ? "Ambiente de testes: a conexão usa uma Shopee simulada (nenhuma loja real é acessada)."
            : "Ambiente de testes: a conexão usa um Mercado Livre simulado (nenhuma conta real é acessada)."}
        </div>
      )}
      {mode === "disabled" && (
        <div className="alert alert-warning py-2">
          A integração com {provider === "shopee" ? "a Shopee" : "o Mercado Livre"} não está habilitada neste
          ambiente.
        </div>
      )}
      {list.isSuccess && accounts.length === 0 && (
        <div className="box card p-3">
          <p className="mb-0 text-muted">
            {provider === "shopee"
              ? "Nenhuma loja conectada. Conecte a loja da Shopee para importar anúncios e pedidos e sincronizar o estoque."
              : "Nenhuma conta conectada. Conecte a conta de vendedor para importar anúncios e pedidos e sincronizar o estoque."}
          </p>
        </div>
      )}
      {accounts.map((a) => (
        <AccountCard key={a.id} account={a} />
      ))}
    </>
  );
}

/** Conta conectada: situação, ações (importar, buscar, sincronizar, desconectar) e configuração. */
export function AccountCard({ account: a }: { account: MarketplaceAccountView }) {
  const { companyId, me, api } = useSession();
  const ui = PROVIDER_UI[a.provider];
  const connectLabel = `Conectar ${ui.unit.toLowerCase()}`;
  const perms = me.permissions as Permissions;
  const canUpdate = can(perms, "marketplaces", "update") && a.status !== "disconnected";
  const canDelete = can(perms, "marketplaces", "delete") && a.status !== "disconnected";
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState<MarketplaceAccountSettingsInput>(() => settingsOf(a));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    setV(settingsOf(a));
  }, [a]);
  const warehouses = useQuery({
    queryKey: ["warehouses", companyId],
    enabled: canUpdate,
    queryFn: () => api<{ data: WarehouseView[] }>("/warehouses"),
  });
  const priceLists = useQuery({
    queryKey: ["price-lists", companyId],
    enabled: canUpdate && can(perms, "price_lists", "read"),
    queryFn: () => api<{ data: PriceListView[] }>("/price-lists"),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["marketplace-accounts", companyId] });
  const set = <K extends keyof MarketplaceAccountSettingsInput>(
    k: K,
    val: MarketplaceAccountSettingsInput[K],
  ) => setV((x) => ({ ...x, [k]: val }));
  const save = useMutation({
    mutationFn: () =>
      api<MarketplaceAccountView>(`/marketplaces/accounts/${a.id}`, {
        method: "PUT",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify(v),
      }),
    onSuccess: () => {
      setErrors({});
      toast("success", "Configuração da conta salva");
      void refresh();
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  const action = useMutation({
    mutationFn: (path: "listings/refresh" | "poll" | "sync") =>
      api<unknown>(`/marketplaces/accounts/${a.id}/${path}`, { method: "POST" }),
    onSuccess: (_r, path) => {
      toast(
        "success",
        path === "listings/refresh"
          ? "Importação dos anúncios iniciada"
          : path === "poll"
            ? "Busca de pedidos iniciada"
            : "Sincronização agendada",
      );
      void refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const disconnect = useMutation({
    mutationFn: () => api<void>(`/marketplaces/accounts/${a.id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Conta desconectada");
      void refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const [tone, label] = ACCOUNT_STATUS[a.status];
  const whs = (warehouses.data?.data ?? []).filter((w) => w.active || v.stockWarehouseIds.includes(w.id));
  return (
    <section className="box card mb-3" aria-label={`Conta ${a.nickname}`}>
      <div className="card-header d-flex flex-wrap align-items-center gap-2">
        <h2 className="h6 mb-0">{a.storeName}</h2>
        <StatusBadge tone={tone}>{label}</StatusBadge>
        {a.sandbox && <StatusBadge tone="info">Testes</StatusBadge>}
        <span className="small">
          {ui.unit} {a.nickname} (id {a.externalUserId})
        </span>
      </div>
      <div className="card-body">
        {a.lastError && <div className="alert alert-warning py-2">{a.lastError}</div>}
        {a.provider === "shopee" &&
          a.status === "active" &&
          a.authExpiresAt &&
          shopeeAuthExpiringSoon(a.authExpiresAt) && (
            <div className="alert alert-warning py-2">
              A autorização da loja vence em {dateTime(a.authExpiresAt)}. Use “Conectar loja” para renovar.
            </div>
          )}
        <p className="small mb-3">
          {a.listings.total} anúncios · {a.listings.linked} vinculados · {a.listings.pending} pendentes de
          sincronização · {a.listings.errors} com erro · {a.ordersInReview} pedidos para revisar
          {a.lastPollAt ? ` · última busca de pedidos ${dateTime(a.lastPollAt)}` : ""}
        </p>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Link
            className="btn btn-outline-secondary btn-sm"
            to="/vendas/marketplace/anuncios"
            search={{ conta: a.id }}
          >
            Ver anúncios
          </Link>
          <Link
            className="btn btn-outline-secondary btn-sm"
            to="/vendas/marketplace/pedidos"
            search={{ conta: a.id }}
          >
            Ver pedidos
          </Link>
          {canUpdate && (
            <>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={action.isPending}
                onClick={() => action.mutate("listings/refresh")}
              >
                Importar anúncios
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={action.isPending}
                onClick={() => action.mutate("poll")}
              >
                Buscar pedidos agora
              </button>
              {(a.syncStock || a.syncPrice) && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={action.isPending}
                  onClick={() => action.mutate("sync")}
                >
                  Sincronizar agora
                </button>
              )}
            </>
          )}
          {a.status === "reauth_required" && (
            <span className="small align-self-center">Use “{connectLabel}” para autorizar de novo.</span>
          )}
          {canDelete && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm ms-auto"
              onClick={() => setConfirm(true)}
            >
              Desconectar
            </button>
          )}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canUpdate) save.mutate();
          }}
        >
          <fieldset disabled={!canUpdate}>
            <div className="row">
              <Field
                label="Nome da loja"
                required
                className="col-md-6"
                error={errors.storeName}
                hint="Canal nos dashboards"
              >
                <input
                  value={v.storeName}
                  maxLength={80}
                  onChange={(e) => set("storeName", e.target.value)}
                />
              </Field>
              <Field
                label="Depósito dos pedidos"
                className="col-md-6"
                error={errors.orderWarehouseId}
                hint="Reserva e baixa do estoque dos pedidos importados"
              >
                <select
                  className="form-select"
                  value={v.orderWarehouseId ?? ""}
                  onChange={(e) => set("orderWarehouseId", e.target.value || null)}
                >
                  <option value="">Depósito padrão</option>
                  {whs.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="form-check mb-2">
              <input
                id={`ml-import-${a.id}`}
                type="checkbox"
                className="form-check-input"
                checked={v.importOrders}
                onChange={(e) => set("importOrders", e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`ml-import-${a.id}`}>
                Importar pedidos pagos como pedidos de venda
              </label>
            </div>
            <div className="form-check mb-3">
              <input
                id={`ml-reserve-${a.id}`}
                type="checkbox"
                className="form-check-input"
                checked={v.reserveOnImport}
                onChange={(e) => set("reserveOnImport", e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`ml-reserve-${a.id}`}>
                Reservar estoque ao importar (pedido “Em andamento”)
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                id={`ml-stock-${a.id}`}
                type="checkbox"
                className="form-check-input"
                checked={v.syncStock}
                onChange={(e) => set("syncStock", e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`ml-stock-${a.id}`}>
                Sincronizar estoque dos anúncios vinculados (disponível = físico − reservado)
              </label>
            </div>
            {v.syncStock && (
              <fieldset className="mb-3 ms-4">
                <legend className="form-label small mb-1">
                  Depósitos somados (nenhum = depósito padrão)
                </legend>
                {whs.map((w) => (
                  <div className="form-check form-check-inline" key={w.id}>
                    <input
                      id={`ml-wh-${a.id}-${w.id}`}
                      type="checkbox"
                      className="form-check-input"
                      checked={v.stockWarehouseIds.includes(w.id)}
                      onChange={(e) =>
                        set(
                          "stockWarehouseIds",
                          e.target.checked
                            ? [...v.stockWarehouseIds, w.id]
                            : v.stockWarehouseIds.filter((x) => x !== w.id),
                        )
                      }
                    />
                    <label className="form-check-label" htmlFor={`ml-wh-${a.id}-${w.id}`}>
                      {w.name}
                    </label>
                  </div>
                ))}
                {errors.stockWarehouseIds && (
                  <div className="text-danger small">{errors.stockWarehouseIds}</div>
                )}
              </fieldset>
            )}
            <div className="form-check mb-2">
              <input
                id={`ml-price-${a.id}`}
                type="checkbox"
                className="form-check-input"
                checked={v.syncPrice}
                onChange={(e) => set("syncPrice", e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`ml-price-${a.id}`}>
                Sincronizar preço (exige permissão para alterar produtos)
              </label>
            </div>
            {v.syncPrice && (
              <div className="row ms-3">
                <Field
                  label="Lista de preço"
                  className="col-md-6"
                  error={errors.priceListId}
                  hint="Nenhuma = preço do produto"
                >
                  <select
                    className="form-select"
                    value={v.priceListId ?? ""}
                    onChange={(e) => set("priceListId", e.target.value || null)}
                  >
                    <option value="">Preço de venda do produto</option>
                    {a.priceListId && !priceLists.data?.data.some((p) => p.id === a.priceListId) && (
                      <option value={a.priceListId}>{a.priceListName ?? "Lista atual"}</option>
                    )}
                    {(priceLists.data?.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
            {canUpdate && (
              <button type="submit" className="btn btn-primary" disabled={save.isPending}>
                Salvar configuração
              </button>
            )}
          </fieldset>
        </form>
      </div>
      <ConfirmDialog
        open={confirm}
        title="Desconectar conta"
        message={
          a.provider === "mercadolivre"
            ? "Os tokens são apagados e a autorização é revogada no Mercado Livre. Anúncios, vínculos e pedidos importados continuam no sistema."
            : a.provider === "shopee"
              ? "Os tokens são apagados e a loja deixa de ser acessada. Para cancelar a autorização na Shopee, use a Central do Vendedor. Anúncios, vínculos e pedidos importados continuam no sistema."
              : `Os tokens são apagados e a ${ui.unit.toLowerCase()} deixa de ser acessada. Para cancelar a autorização ${marketplaceIn(a.provider)}, remova o aplicativo no painel do canal. Anúncios, vínculos e pedidos importados continuam no sistema.`
        }
        confirmLabel="Desconectar"
        onConfirm={() => {
          setConfirm(false);
          disconnect.mutate();
        }}
        onCancel={() => setConfirm(false)}
      />
    </section>
  );
}
